/**
 * 语言路由工具函数
 * 处理带语言前缀的路由跳转和路径生成
 */

import { getSupportedLanguageCodes, isLanguageSupported } from './language';

/**
 * 从 URL 路径中提取语言代码
 * @param {string} pathname - URL 路径，如 "/en/home" 或 "/home"
 * @returns {string} 语言代码，如果未找到则返回默认语言 'en'
 */
export const getLanguageFromPath = (pathname: string): string => {
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0];

  // 检查第一个路径段是否是支持的语言代码
  if (firstSegment && isLanguageSupported(firstSegment)) {
    return firstSegment;
  }

  return 'en'; // 默认返回英语
};

/**
 * 从 URL 路径中移除语言前缀
 * @param {string} pathname - URL 路径，如 "/en/home"
 * @returns {string} 移除语言前缀后的路径，如 "/home"
 */
export const removeLanguagePrefix = (pathname: string): string => {
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0];

  if (firstSegment && isLanguageSupported(firstSegment)) {
    // 移除第一个语言段
    return '/' + segments.slice(1).join('/');
  }

  return pathname;
};

/**
 * 为路径添加语言前缀
 * @param {string} path - 原始路径，如 "/home"
 * @param {string} language - 语言代码，如 "en"
 * @returns {string} 带语言前缀的路径，如 "/en/home"
 */
export const addLanguagePrefix = (path: string, language: string): string => {
  // 移除可能已存在的语言前缀
  const cleanPath = removeLanguagePrefix(path);

  // 确保路径以 / 开头
  const normalizedPath = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;

  return `/${language}${normalizedPath}`;
};

/**
 * 切换当前路径的语言
 * @param {string} currentPath - 当前路径，如 "/en/home"
 * @param {string} newLanguage - 新语言代码，如 "ko"
 * @returns {string} 新的路径，如 "/ko/home"
 */
export const switchLanguageInPath = (currentPath: string, newLanguage: string): string => {
  const pathWithoutLanguage = removeLanguagePrefix(currentPath);
  return addLanguagePrefix(pathWithoutLanguage, newLanguage);
};

/**
 * 获取所有支持的语言代码的正则表达式模式
 * @returns {string} 如 "(en|ko|id)"
 */
export const getLanguagePattern = (): string => {
  const codes = getSupportedLanguageCodes();
  return `(${codes.join('|')})`;
};
